/**
 * DOCUMENTATION:
 * Component: Html5WebStorage
 * Purpose: Educational page covering the HTML5 Web Storage API (localStorage and sessionStorage).
 * * * Note for Junior Developers:
 * - Before HTML5, developers had to use tiny, slow 'Cookies' to save data on the client's browser.
 * - The Web Storage API provides much more space (typically 5MB+) and doesn't slow down network
 * requests because the data isn't sent to the server automatically.
 * - Remember: Web Storage ONLY saves strings. You must serialize objects using JSON.stringify()!
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function Html5WebStorage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Web Storage Methods reference table
    const storageMethods = useMemo(() => [
        { method: "setItem(key, value)", purpose: "Save Data", description: "Saves a key/value pair in the browser. Both key and value must be strings." },
        { method: "getItem(key)", purpose: "Retrieve Data", description: "Fetches the value associated with the provided key. Returns 'null' if the key doesn't exist." },
        { method: "removeItem(key)", purpose: "Delete Specific Data", description: "Removes a specific key/value pair from the storage object." },
        { method: "clear()", purpose: "Wipe All Data", description: "Completely empties all key/value pairs for that specific domain." },
    ], []);

    // Coding examples demonstrating local vs session storage
    const codingModules = useMemo(() => [
        {
            title: "1. Local Storage (Persistent)",
            code: `// Saving a user's theme preference\nlocalStorage.setItem("theme", "dark");\n\n// Retrieving the preference later (even after browser restart)\nconst currentTheme = localStorage.getItem("theme");\n\nif (currentTheme === "dark") {\n  document.body.classList.add("dark-mode");\n}`,
            desc: "Data saved in localStorage has no expiration date. It remains in the user's browser until explicitly deleted via JavaScript or cleared by the user in their browser settings."
        },
        {
            title: "2. Session Storage (Temporary)",
            code: `// Saving a draft of a form response\nsessionStorage.setItem("formDraft", "I am having an issue with...");\n\n// Retrieving the draft to populate the textarea\ndocument.getElementById("messageBox").value = sessionStorage.getItem("formDraft");\n\n// Clear the draft once the form is successfully submitted\nsessionStorage.removeItem("formDraft");`,
            desc: "Data in sessionStorage is tied to the page session. It survives page reloads and restores, but is immediately deleted as soon as the user closes that specific browser tab."
        },
        {
            title: "3. Handling Complex Objects (JSON Serialization)",
            code: `// ❌ BAD: Web Storage will convert the object to "[object Object]"\nconst user = { name: "Alice", role: "Admin" };\nlocalStorage.setItem("user", user);\n\n// ✅ GOOD: Serialize to a JSON string first\nlocalStorage.setItem("user", JSON.stringify(user));\n\n// Later, parse it back into a usable JavaScript object\nconst savedUserData = localStorage.getItem("user");\nconst parsedUser = JSON.parse(savedUserData);\n\nconsole.log(parsedUser.name); // Outputs: "Alice"`,
            desc: "This is a common pitfall! Because Web Storage only accepts strings, you must use JSON.stringify() to save arrays or objects, and JSON.parse() to turn them back into usable data structures when retrieving them."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    WEB STORAGE API
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Giving the Browser a Memory
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            The HTML5 Web Storage API provides two secure mechanisms for storing key/value pairs on the client side: <code>localStorage</code> and <code>sessionStorage</code>. Unlike cookies, this data is never transferred to the server automatically with every HTTP request. This makes Web Storage much more secure and capable of handling significantly larger amounts of data (up to 5MB per domain).
                        </Typography>

                        {/* ✅ Storage Methods Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Core API Methods</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Method</div>
                                    <div>Primary Purpose</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {storageMethods.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.method}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.purpose}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/html5-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
